import { t } from '../lib/i18n';
import { NavLink, useLocation } from 'react-router-dom';
import { Suspense, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { KeepAliveOutlet } from './KeepAliveOutlet';
import { RouteErrorBoundary } from './RouteErrorBoundary';
import { fetchAttentionModel, fetchDashboard, peekAttention, QK, subscribeAttention } from '../lib/owner-queries';
import { cachedGet, peekCache } from '../lib/query-cache';
import type { AttentionReadModel, Dashboard as DashboardData } from '../lib/api';
import { AnimatePresence, LazyMotion, domAnimation, m, useReducedMotion } from 'framer-motion';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { QuickAdd } from './QuickAdd';
import { GlobalSearch } from './GlobalSearch';
import { useModalFocus } from '../lib/modal-focus';
import { isDarkTheme, setThemeChoice, useThemeChoice } from '../lib/theme';
import { workspaceComposerHref } from '../lib/workspace';
import {
  EMPTY_COUNTS,
  HIDE_ON_MOBILE_PATHS,
  MOBILE_PRIMARY_PATHS,
  badgeFor,
  attentionCountsFrom,
  groupNav,
  type AttentionCounts,
  ATTENTION_CHANGED_EVENT,
} from '../lib/nav';

interface NavItem {
  to: string;
  /** Действие входа; группировка использует основной путь. */
  entryTo?: string;
  label: string;
  icon: ReactNode;
  adminOnly?: boolean;
  hideOnMobile?: boolean;
  /** Подпись в нижнем ряду телефона, где мало места. */
  shortLabel?: string;
}

const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

/*
  Порядок и группировка меню живут в lib/nav.ts — там же их проверяют тесты.
  Здесь только пункты: путь, подпись и иконка. Подпись всегда через t(),
  иначе на английской локали остаётся русский текст.
*/
const NAV: NavItem[] = [
  {
    to: '/',
    label: 'Сегодня',
    adminOnly: true,
    icon: (
      <svg viewBox="0 0 24 24" {...stroke}>
        <path d="M4 11 12 4l8 7" />
        <path d="M6 10v9h12v-9" />
      </svg>
    ),
  },
  {
    to: '/workspace', label: 'Поручить', adminOnly: true,
    icon: <svg viewBox="0 0 24 24" {...stroke}><path d="M12 5v14M5 12h14" /></svg>,
  },
  {
    to: '/work', label: 'Ход работы', adminOnly: true,
    icon: <svg viewBox="0 0 24 24" {...stroke}><path d="M4 19h16M7 15V9M12 15V5M17 15v-4" /></svg>,
  },
  {
    to: '/runs', label: 'Поручения', adminOnly: true,
    icon: <svg viewBox="0 0 24 24" {...stroke}><path d="M6 5h12v15H6zM9 9h6M9 13h6M9 17h3" /></svg>,
  },
  {
    to: '/control-plane', label: 'Согласования', adminOnly: true,
    icon: <svg viewBox="0 0 24 24" {...stroke}><path d="m5 12 4 4L19 6" /></svg>,
  },
  {
    to: '/inbox',
    label: 'Входящие',
    adminOnly: true,
    icon: (
      <svg viewBox="0 0 24 24" {...stroke}>
        <path d="M4 5.5h16v13H4z" />
        <path d="M4 15h4l1.5 2h5L16 15h4" />
      </svg>
    ),
  },
  {
    to: '/leads',
    label: 'Заявки',
    adminOnly: true,
    icon: (
      <svg viewBox="0 0 24 24" {...stroke}>
        <path d="M5 7h14M5 12h10M5 17h14" />
        <circle cx="18" cy="12" r="1.5" />
      </svg>
    ),
  },
  {
    to: '/bookings',
    label: 'Бронирования',
    shortLabel: 'Брони',
    adminOnly: true,
    icon: <svg viewBox="0 0 24 24" {...stroke}><rect x="4" y="5" width="16" height="16" rx="2" /><path d="M8 3v4M16 3v4M4 10h16m-11 5 2 2 4-4" /></svg>,
  },
  {
    to: '/calendar',
    label: 'Календарь',
    icon: (
      <svg viewBox="0 0 24 24" {...stroke}>
        <rect x="3.5" y="5.5" width="17" height="15" rx="2" />
        <path d="M3.5 10h17M8 3.5v4M16 3.5v4" />
      </svg>
    ),
  },
  {
    to: '/objects',
    label: 'Объекты',
    adminOnly: true,
    icon: (
      <svg viewBox="0 0 24 24" {...stroke}>
        <path d="M5 20V5h14v15M8 8h3M8 12h3M14 8h2M14 12h2M10 20v-4h4v4" />
      </svg>
    ),
  },
  {
    to: '/market-intelligence',
    label: 'Маркет',
    adminOnly: true,
    icon: (
      <svg viewBox="0 0 24 24" {...stroke}>
        <path d="M3.5 18.5h17M5.5 15.5l5-5 3.5 3 5-7" />
        <path d="M15.5 6.5H19v3.5" />
      </svg>
    ),
  },
  {
    to: '/cleaning',
    label: 'Уборки и счётчики',
    adminOnly: true,
    icon: (
      <svg viewBox="0 0 24 24" {...stroke}>
        <path d="M6 20h12M8 20V9l4-4 4 4v11M10 13h4M10 16h4" />
      </svg>
    ),
  },
  {
    to: '/tm30',
    label: 'Регистрация гостей',
    shortLabel: 'Регистрация',
    adminOnly: true,
    icon: (
      <svg viewBox="0 0 24 24" {...stroke}>
        <path d="M6 3.5h9l3 3V20H6zM14.5 3.5V7H18M9 11h6M9 15h6" />
      </svg>
    ),
  },
  {
    to: '/transfers',
    label: 'Трансферы',
    adminOnly: true,
    icon: (
      <svg viewBox="0 0 24 24" {...stroke}>
        <path d="M4 15V9h12l4 4v2M7 18.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3ZM17 18.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3ZM16 9v4h4" />
      </svg>
    ),
  },
  {
    to: '/money',
    label: 'Деньги',
    adminOnly: true,
    icon: (
      <svg viewBox="0 0 24 24" {...stroke}>
        <rect x="3" y="6.5" width="18" height="12" rx="2" />
        <circle cx="12" cy="12.5" r="2.5" />
        <path d="M6.5 12.5h.01M17.5 12.5h.01" />
      </svg>
    ),
  },
  {
    to: '/owners',
    label: 'Владельцы',
    adminOnly: true,
    icon: (
      <svg viewBox="0 0 24 24" {...stroke}>
        <circle cx="9" cy="8.5" r="2.5" />
        <path d="M4 18.5c1.2-2.4 3-3.5 5-3.5s3.8 1.1 5 3.5" />
        <circle cx="16.5" cy="9.5" r="2" />
        <path d="M15.5 15.4c1.9.2 3.2 1.3 4.2 3.1" />
      </svg>
    ),
  },
  {
    to: '/team',
    label: 'Агенты',
    adminOnly: true,
    icon: (
      <svg viewBox="0 0 24 24" {...stroke}>
        <circle cx="9" cy="9" r="2.5" />
        <circle cx="16" cy="10" r="2" />
        <path d="M4.5 18c1.2-2.2 3-3.3 4.5-3.3S12.3 15.8 13.5 18M13 18c.7-1.3 1.8-2 3-2s2.4.7 3.2 2" />
      </svg>
    ),
  },
  {
    to: '/tasks',
    label: 'Задачи',
    icon: (
      <svg viewBox="0 0 24 24" {...stroke}>
        <path d="M4 7h10M4 12h10M4 17h6" />
        <path d="m17 6 2 2 3-3" />
      </svg>
    ),
  },
  {
    to: '/notes',
    label: 'Заметки',
    icon: (
      <svg viewBox="0 0 24 24" {...stroke}>
        <path d="M6 3.5h9l4 4V20a.5.5 0 0 1-.5.5h-12A.5.5 0 0 1 6 20V4a.5.5 0 0 1 .5-.5Z" />
        <path d="M14.5 3.5V8h4.5M9 13h6M9 16.5h4" />
      </svg>
    ),
  },
  {
    to: '/documents',
    label: 'Документы',
    adminOnly: true,
    icon: (
      <svg viewBox="0 0 24 24" {...stroke}>
        <path d="M6 3.5h8l4 4V20a.5.5 0 0 1-.5.5h-11A.5.5 0 0 1 6 20V4a.5.5 0 0 1 .5-.5Z" />
        <path d="M14 3.5V8h4M9 12h6M9 15.5h6" />
      </svg>
    ),
  },
  {
    to: '/waiting',
    label: 'Жду ответа',
    adminOnly: true,
    icon: (
      <svg viewBox="0 0 24 24" {...stroke}>
        <circle cx="12" cy="12" r="8" />
        <path d="M12 7.5V12l3 2" />
      </svg>
    ),
  },
  {
    to: '/decisions',
    label: 'Решения',
    adminOnly: true,
    icon: (
      <svg viewBox="0 0 24 24" {...stroke}>
        <path d="M6 4.5h12v15H6z" />
        <path d="M9 9h6M9 12.5h6M9 16h3" />
      </svg>
    ),
  },
];

const SECONDARY: NavItem[] = [
  {
    to: '/settings',
    label: 'Профиль и настройки',
    icon: (
      <svg viewBox="0 0 24 24" {...stroke}>
        <circle cx="12" cy="12" r="3" />
        <path d="M12 3.5v2.2M12 18.3v2.2M20.5 12h-2.2M5.7 12H3.5M18 6l-1.6 1.6M7.6 16.4 6 18M18 18l-1.6-1.6M7.6 7.6 6 6" />
      </svg>
    ),
  },
];

interface LeadWorkspaceCounts {
  summary?: { new_count?: number };
}

function countsFromDashboard(data: Pick<DashboardData, 'overdue' | 'dueToday' | 'waitingFollowUp' | 'decisionsNeedingContext'>): Partial<AttentionCounts> {
  return {
    tasksDue: (data.overdue?.length ?? 0) + (data.dueToday?.length ?? 0),
    waiting: data.waitingFollowUp ?? 0,
    decisions: data.decisionsNeedingContext?.length ?? 0,
  };
}

function initialAttentionCounts(): AttentionCounts {
  const attention = peekAttention();
  const dashboard = peekCache<DashboardData>(QK.dashboard);
  const leads = peekCache<LeadWorkspaceCounts>(QK.leadsWorkspace);
  return {
    ...EMPTY_COUNTS,
    ...(attention ? attentionCountsFrom(attention) : {}),
    ...(dashboard ? countsFromDashboard(dashboard) : {}),
    ...(leads ? { leads: Math.max(0, leads.summary?.new_count ?? 0) } : {}),
  };
}

/*
  Счётчики внимания берутся из тех же read-моделей, что и сами экраны.
  Для Inbox считаем именно строки после той же группировки, которую видит
  пользователь во /inbox. Ошибка сети не должна ломать навигацию:
  молча оставляем нули, меню просто без бейджей.
*/
function useAttentionCounts(enabled: boolean): AttentionCounts {
  const [counts, setCounts] = useState<AttentionCounts>(initialAttentionCounts);
  // Inbox mutations («Разобрано», owner) fire ATTENTION_CHANGED_EVENT: reload the badges.
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const bump = () => setVersion((value) => value + 1);
    window.addEventListener(ATTENTION_CHANGED_EVENT, bump);
    return () => window.removeEventListener(ATTENTION_CHANGED_EVENT, bump);
  }, []);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    void fetchDashboard({ force: version > 0 })
      .then((data) => {
        if (!alive) return;
        setCounts((prev) => ({
          ...prev,
          ...countsFromDashboard(data),
        }));
      })
      .catch(() => undefined);
    const unsubscribe = subscribeAttention((data) => {
      if (!alive || !Array.isArray(data?.requires_you)) return;
      setCounts((prev) => ({ ...prev, ...attentionCountsFrom(data) }));
    });
    void fetchAttentionModel({ force: version > 0 })
      .then((data: AttentionReadModel) => {
        if (!alive || !Array.isArray(data?.requires_you)) return;
        setCounts((prev) => ({ ...prev, ...attentionCountsFrom(data) }));
      })
      .catch(() => undefined);
    void cachedGet(QK.leadsWorkspace, () => api.get<LeadWorkspaceCounts>('/leads/workspace'), { force: version > 0 })
      .then((data) => {
        if (!alive) return;
        setCounts((prev) => ({ ...prev, leads: Math.max(0, data.summary?.new_count ?? 0) }));
      })
      .catch(() => undefined);
    return () => {
      alive = false;
      unsubscribe();
    };
  }, [enabled, version]);
  return counts;
}

function NavBadge({ count, warning = false }: { count: number; warning?: boolean }) {
  return (
    <span
      className={`jack-nav-badge shrink-0 rounded-full px-1.5 py-0.5 text-[0.6875rem] tabular-nums ${warning ? 'jack-nav-badge--warning' : 'jack-nav-badge--problem'}`}
      aria-label={t('Требует внимания: {count}', { count })}
    >
      {count}
    </span>
  );
}

function ThemeToggle({ compact = false }: { compact?: boolean }) {
  useThemeChoice();
  const dark = isDarkTheme();
  return (
    <button
      type="button"
      onClick={() => setThemeChoice(dark ? 'light' : 'dark')}
      className={`jack-theme-toggle ${compact ? 'jack-footer-button' : 'flex min-h-11 items-center gap-2 rounded-lg px-3 text-sm text-muted transition-colors hover:bg-surface-3 hover:text-ink'}`}
      aria-pressed={dark}
      aria-label={compact ? (dark ? t('Светлая тема') : t('Тёмная тема')) : (dark ? t('Включить светлую тему') : t('Включить тёмную тему'))}
      title={compact ? (dark ? t('Светлая тема') : t('Тёмная тема')) : undefined}
    >
      <svg viewBox="0 0 24 24" className="size-4" {...stroke}>
        {dark ? (
          <>
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v2M12 20v2M22 12h-2M4 12H2M18.4 5.6 17 7M7 17l-1.4 1.4M18.4 18.4 17 17M7 7 5.6 5.6" />
          </>
        ) : (
          <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5Z" />
        )}
      </svg>
      {!compact && <span>{dark ? t('Светлая тема') : t('Тёмная тема')}</span>}
    </button>
  );
}

function SidebarLink({ item, badge }: { item: NavItem; badge: number | null }) {
  return (
    <NavLink
      to={item.entryTo ?? item.to}
      end={item.to === '/'}
      className={({ isActive }) =>
        [
          'jack-nav-link flex items-center gap-2.5 px-2 py-1 text-sm transition-colors',
          item.to === '/workspace' ? 'jack-nav-link--primary' : '',
          isActive ? 'font-medium text-ink' : 'text-muted hover:text-ink',
        ].join(' ')
      }
    >
      {() => (
        <>
          <span className="jack-shell-icon" aria-hidden>{item.icon}</span>
          <span className="min-w-0 flex-1 leading-snug">{t(item.label)}</span>
          {badge !== null && <NavBadge count={badge} warning={item.to === '/waiting' || item.to === '/leads'} />}
        </>
      )}
    </NavLink>
  );
}

export function AppShell() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const reducedMotion = useReducedMotion();
  // Задача, добавленная из любого места, должна сразу появиться в открытом разделе
  const [refreshKey, setRefreshKey] = useState(0);

  const [moreOpen, setMoreOpen] = useState(false);
  const morePanel = useRef<HTMLDivElement>(null);
  useModalFocus(morePanel, moreOpen);
  useEffect(() => {
    if (!moreOpen) return;
    const close = (event: KeyboardEvent) => { if (event.key === 'Escape') setMoreOpen(false); };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [moreOpen]);
  useEffect(() => {
    void import('../pages/JackInbox');
    void import('../pages/Money');
    void import('../pages/Bookings');
    void import('../pages/Objects');
    void import('../pages/Calendar');
    void import('../pages/Leads');
  }, []);
  const canAccessJackOs = user?.role === 'admin';
  const accessibleNav = NAV.filter((item) => !item.adminOnly || canAccessJackOs).map((item) =>
    item.to === '/workspace'
      ? { ...item, entryTo: workspaceComposerHref(location.pathname, location.search) }
      : item,
  );
  const secondaryNav = SECONDARY.filter((item) => !item.adminOnly || canAccessJackOs);
  const counts = useAttentionCounts(canAccessJackOs);

  const sidebarGroups = groupNav(accessibleNav);
  const openGroups = sidebarGroups.filter((group) => !group.collapsed);
  const collapsedGroups = sidebarGroups.filter((group) => group.collapsed);
  const moreActive = [...collapsedGroups.flatMap((group) => group.items), ...secondaryNav]
    .some((item) => location.pathname === item.to || location.pathname.startsWith(item.to + '/'));
  const mobilePrimaryNav = MOBILE_PRIMARY_PATHS.flatMap((path) => {
    const item = accessibleNav.find((nav) => nav.to === path);
    return item ? [item] : [];
  });
  const mobilePrimaryPaths = new Set(mobilePrimaryNav.map((item) => item.to));
  // Browser scroll restoration and anchoring can park the content column
  // a few dozen pixels down, so the page title sits under the top edge
  // while the sticky sidebar stays put. Keep the title in view on entry.
  useLayoutEffect(() => {
    const previous = history.scrollRestoration;
    history.scrollRestoration = 'manual';
    const revealTitle = () => {
      const title = document.querySelector('main h1');
      if (!(title instanceof HTMLElement)) return;
      const top = title.getBoundingClientRect().top;
      if (top >= 12 || top <= -96) return;
      const main = document.querySelector('.jack-owner-main');
      const delta = top - 16;
      if (main instanceof HTMLElement && getComputedStyle(main).overflowY === 'auto') main.scrollTop += delta;
      else window.scrollBy(0, delta);
    };
    revealTitle();
    const later = window.setTimeout(revealTitle, 60);
    return () => {
      window.clearTimeout(later);
      history.scrollRestoration = previous;
    };
  }, [location.pathname]);

  const mobileMoreGroups = groupNav(
    [...accessibleNav, ...secondaryNav].filter(
      (item) => !mobilePrimaryPaths.has(item.to) && !item.hideOnMobile && !HIDE_ON_MOBILE_PATHS.includes(item.to),
    ),
  );

  return (
    <div className="jack-shell min-h-dvh md:flex">
      {/* Боковая навигация: ноутбук и стенд.
          Липкая и со своим скроллом: на длинном списке задач страница
          прокручивается, а панель с разделами остаётся на месте. */}
      <aside lang="ru" className="jack-sidebar hidden w-[260px] shrink-0 flex-col px-3 py-4 md:sticky md:top-0 md:flex md:h-dvh">
        <div className="jack-shell-brand mb-4 flex items-center gap-2.5 px-2">
          <img className="jack-brand-logo" src="/smart-rentals-pattaya.png" alt="Smart Rentals Pattaya" width={2048} height={683} />
        </div>

        {/* Поиск по всему пространству: задачи, заметки, события, файлы. */}
        <div className="mb-4 px-2">
          <GlobalSearch />
        </div>

        <nav className="jack-sidebar-sections flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto" aria-label="Разделы">
          {openGroups.map((group) => (
            <div key={group.id}>
              <p className="jack-nav-group">{t(group.title)}</p>
              {group.items.map((item) => <SidebarLink key={item.to} item={item} badge={badgeFor(item.to, counts)} />)}
            </div>
          ))}

          <details className="mt-2" open={moreActive || undefined}>
            <summary className="jack-nav-link flex cursor-pointer select-none items-center gap-2.5 px-2 py-1.5 text-sm font-medium text-muted hover:text-ink">
              <span className="living-icon living-icon-more" aria-hidden>
                <svg viewBox="0 0 24 24" {...stroke}>
                  <circle cx="5" cy="12" r="1.2" />
                  <circle cx="12" cy="12" r="1.2" />
                  <circle cx="19" cy="12" r="1.2" />
                </svg>
              </span>
              {t('Ещё')}
            </summary>
            <div className="mt-1">
              {collapsedGroups.flatMap((group) => group.items).map((item) => (
                <SidebarLink key={item.to} item={item} badge={badgeFor(item.to, counts)} />
              ))}
              {secondaryNav.map((item) => <SidebarLink key={item.to} item={item} badge={null} />)}
            </div>
          </details>
        </nav>

        <div className="jack-sidebar-footer shrink-0">
          <span className="jack-user-avatar" aria-hidden>{user?.name.trim().charAt(0).toLocaleUpperCase('ru-RU')}</span>
          <span className="min-w-0 flex-1 truncate text-sm text-ink" title={user?.name}>{user?.name}</span>
          <ThemeToggle compact />
          <button
            type="button"
            onClick={() => void logout()}
            className="jack-footer-button"
            title={t('Выйти')}
            aria-label={t('Выйти')}
          >
            <svg viewBox="0 0 24 24" className="size-4" {...stroke} aria-hidden>
              <path d="M9 5H5v14h4M13 8l4 4-4 4M9 12h11" />
            </svg>
          </button>
        </div>
      </aside>

      {/* Контент */}
      <main className="jack-owner-main min-w-0 flex-1">
        <RouteErrorBoundary>
        <Suspense fallback={<div role="status" className="px-6 py-8 text-sm text-muted">Загружаю раздел…</div>}>
          <KeepAliveOutlet key={refreshKey} />
        </Suspense>
        </RouteErrorBoundary>
      </main>

      {canAccessJackOs && <QuickAdd onAdded={() => setRefreshKey((k) => k + 1)} />}

      {/* На телефоне четыре главных раздела + «Ещё». */}
      <nav
        lang="ru"
        className={[
          'jack-mobile-nav z-10 grid shrink-0 bg-surface pb-[env(safe-area-inset-bottom)] md:hidden',
          moreOpen ? 'invisible' : '',
          mobilePrimaryNav.length === 4 ? 'grid-cols-5' : 'grid-cols-4',
        ].join(' ')}
      >
        {mobilePrimaryNav.map((item) => {
          const badge = badgeFor(item.to, counts);
          return (
            <NavLink
              key={item.to}
              to={item.entryTo ?? item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                [
                  'jack-mobile-tab relative flex min-w-0 flex-col items-center gap-1 px-0.5 py-2 text-[0.6875rem] leading-tight transition-colors',
                  isActive ? 'text-accent' : 'text-muted',
                ].join(' ')
              }
            >
              {() => (
                <>
                  <span className="jack-shell-icon" aria-hidden>{item.icon}</span>
                  <span className="jack-mobile-nav__label">{t(item.shortLabel ?? item.label)}</span>
                  {badge !== null && (
                    <span className="absolute right-0.5 top-1">
                      <NavBadge count={badge} warning={item.to === '/leads'} />
                    </span>
                  )}
                </>
              )}
            </NavLink>
          );
        })}

        {/* Ещё: Входящие и остальное, что не в основном ряду */}
        <button
          type="button"
          aria-expanded={moreOpen}
          aria-controls="mobile-more"
          onClick={() => setMoreOpen((v) => !v)}
          className={`jack-mobile-tab flex min-w-0 flex-col items-center gap-1 px-0.5 py-2 text-[0.6875rem] leading-tight ${
            moreOpen ? 'text-accent' : 'text-muted'
          }`}
        >
          <span className="living-icon living-icon-more">
            <svg viewBox="0 0 24 24" {...stroke}>
              <circle cx="5" cy="12" r="1.2" />
              <circle cx="12" cy="12" r="1.2" />
              <circle cx="19" cy="12" r="1.2" />
            </svg>
          </span>
          <span className="jack-mobile-nav__label">Ещё</span>
        </button>
      </nav>

      <LazyMotion features={domAnimation} strict>
        <AnimatePresence>
          {moreOpen && (
            <m.div
            className="fixed inset-0 z-20 md:hidden"
            initial={reducedMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reducedMotion ? 0 : 0.16 }}
          >
            <button
              type="button"
              aria-label={t('Закрыть')}
              onClick={() => setMoreOpen(false)}
              className="jack-shell-backdrop absolute inset-0"
            />
            <m.div
              ref={morePanel}
              id="mobile-more"
              lang="ru"
              role="dialog"
              aria-modal="true"
              aria-label="Ещё"
              tabIndex={-1}
              initial={reducedMotion ? false : { y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: reducedMotion ? 0 : '100%' }}
              transition={reducedMotion ? { duration: 0 } : { type: 'spring', stiffness: 420, damping: 38, mass: 0.72 }}
              className="jack-more-panel absolute inset-x-0 bottom-0 max-h-[92dvh] overflow-x-hidden overflow-y-auto overscroll-contain rounded-t-card border-t border-line bg-surface px-3 pb-[max(1rem,env(safe-area-inset-bottom))]"
            >
            <div className="sticky top-0 z-10 bg-surface px-2 pt-2">
              <div className="mx-auto mb-2 h-1.5 w-10 rounded-full bg-line" aria-hidden />
              <div className="flex items-center justify-between pb-2">
                <h2 className="font-display text-xl font-semibold">Ещё</h2>
                <button type="button" onClick={() => setMoreOpen(false)} className="min-h-11 rounded-lg px-3 text-base text-accent">{t('Закрыть')}</button>
              </div>
            </div>

            <div className="mb-3 px-2">
              <GlobalSearch />
            </div>
            {canAccessJackOs && (
              <NavLink
                to="/market-intelligence"
                onClick={() => setMoreOpen(false)}
                className="jack-market-shortcut mx-2 mb-3 flex min-h-12 items-center gap-3 rounded-xl border px-3 py-2 focus-visible:outline-2 focus-visible:outline-offset-2"
              >
                <span className="font-semibold">Маркет</span>
                <span className="min-w-0 flex-1 text-sm">Цены · спрос · конкуренты</span>
                <span aria-hidden="true">→</span>
              </NavLink>
            )}

            {mobileMoreGroups.map((group) => (
              <div key={group.id}>
                <p className="jack-nav-group">{t(group.title)}</p>
                <div className="divide-y divide-line pb-2">
                  {group.items.map((item) => {
                    const badge = badgeFor(item.to, counts);
                    return (
                      <NavLink
                        key={item.to}
                        to={item.entryTo ?? item.to}
                        onClick={() => setMoreOpen(false)}
                        className={`jack-nav-link flex min-h-12 min-w-0 items-center gap-3 px-3 py-2 text-base text-ink ${item.to === '/workspace' ? 'jack-nav-link--primary' : ''}`}
                      >
                        <span className="jack-shell-icon" aria-hidden>{item.icon}</span>
                        <span className="min-w-0 flex-1 leading-snug">{t(item.label)}</span>
                        {badge !== null && <NavBadge count={badge} warning={item.to === '/waiting' || item.to === '/leads'} />}
                        <span className="text-muted" aria-hidden>→</span>
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            ))}
            {secondaryNav.length > 0 && (
              <div>
                <p className="jack-nav-group">{t('Сервис')}</p>
                <div className="divide-y divide-line pb-2">
                  {secondaryNav.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.entryTo ?? item.to}
                      onClick={() => setMoreOpen(false)}
                      className={`jack-nav-link flex min-h-12 min-w-0 items-center gap-3 px-3 py-2 text-base text-ink ${item.to === '/workspace' ? 'jack-nav-link--primary' : ''}`}
                    >
                      <span className="jack-shell-icon" aria-hidden>{item.icon}</span>
                      <span className="min-w-0 flex-1 leading-snug">{t(item.label)}</span>
                      <span className="text-muted" aria-hidden>→</span>
                    </NavLink>
                  ))}
                </div>
              </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-2 px-2 py-3.5">
              <span className="text-sm text-muted">{user?.name}</span>
              <ThemeToggle />
              <button
                type="button"
                onClick={() => void logout()}
                className="inline-flex min-h-11 items-center px-3 text-sm text-muted underline underline-offset-2"
              >
                Выйти
              </button>
            </div>
            </m.div>
          </m.div>
          )}
        </AnimatePresence>
      </LazyMotion>
    </div>
  );
}
